import React, { useState, useEffect, useMemo } from 'react';
import {
    Card,
    Row,
    Col,
    Typography,
    Button,
    Select,
    Space,
    message,
    Modal,
    Skeleton,
    Divider,
    Table,
    Popconfirm,
} from 'antd';
import { useHistory, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { terminalFields } from '../../../constants/terminalFields';

import axiosInstance from '../../../configs';
import InvoiceHeader from './Shared/InvoiceHeader';
import CustomerSection from './Shared/CustomerSection';
import ServiceTable from './Shared/ServiceTable';
import TotalSummary from './Shared/TotalSummary';

const { Title, Text } = Typography;

export default function HandlingEdit() {
    const history = useHistory();
    const { id } = useParams();

    const [, setLoading] = useState(false);
    const [initLoading, setInitLoading] = useState(true);
    const [invoiceData, setInvoiceData] = useState(null);

    const [contacts, setContacts] = useState([]);
    const [costs, setCosts] = useState([]);
    const [filteredCosts, setFilteredCosts] = useState([]);

    const [selectedContact, setSelectedContact] = useState(null);
    const [lineItems, setLineItems] = useState([]);
    const [selectedServiceId, setSelectedServiceId] = useState('');
    const [statusGrup, setStatusGrup] = useState('');

    const [confirmOpen, setConfirmOpen] = useState(false);

    // DETIL ITEM state
    const [nopenItems, setNopenItems] = useState([]); // available items from item_stock
    const [invoiceItems, setInvoiceItems] = useState([]); // saved items in finance_invoice_item
    const [selectedItemCode, setSelectedItemCode] = useState('');

    useEffect(() => {
        fetchReferences();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchReferences = async () => {
        setInitLoading(true);
        try {
            let contactsData = [];

            try {
                const resContacts = await axiosInstance.get('/finance/contacts');
                contactsData = resContacts.data.data || [];
            } catch (e) { /* empty */ }

            setContacts(contactsData);

            if (id) {
                const resInv = await axiosInstance.get(`/finance/sales-invoices/${id}`);
                const inv = resInv.data.data;
                if (inv) {
                    setInvoiceData(inv);

                    // Fetch costs filtered by id_customer
                    try {
                        const resCosts = await axiosInstance.get('/finance/costs', { params: { idCustomer: inv.id_customer } });
                        setCosts(resCosts.data.data || []);
                    } catch (e) { /* empty */ }

                    // Load saved invoice_items
                    if (inv.invoice_items) {
                        setInvoiceItems(inv.invoice_items);
                    }

                    // Fetch available nopen items
                    if (inv.nopen) {
                        try {
                            const resItems = await axiosInstance.get(`/finance/nopen-items/${inv.nopen}`);
                            setNopenItems(resItems.data.data || []);
                        } catch (e) { /* empty */ }
                    }
                }
            }
        } catch (err) {
            /* empty */
        } finally {
            setInitLoading(false);
        }
    };

    const initialValues = useMemo(() => {
        if (!invoiceData) return {
            invoiceNumber: '',
            invoiceDate: dayjs(),
            dueDate: dayjs(),
            periodStart: dayjs(),
            periodEnd: dayjs(),
            documentNumber: '',
            documentDate: dayjs(),
            terminal: ''
        };
        return {
            invoiceNumber: invoiceData.invoice_number,
            invoiceDate: invoiceData.invoice_date ? dayjs(invoiceData.invoice_date) : dayjs(),
            dueDate: invoiceData.due_date ? dayjs(invoiceData.due_date) : dayjs(),
            periodStart: invoiceData.period_start ? dayjs(invoiceData.period_start) : null,
            periodEnd: invoiceData.period_end ? dayjs(invoiceData.period_end) : null,
            documentNumber: invoiceData.nopen || '',
            documentDate: invoiceData.document_date ? dayjs(invoiceData.document_date) : null,
            terminal: invoiceData.terminal || ''
        };
    }, [invoiceData]);

    const [values, setValues] = useState(initialValues);

    useEffect(() => {
        setValues(initialValues);
    }, [initialValues]);

    const setFieldValue = (field, value) => {
        setValues(prev => ({ ...prev, [field]: value }));
    };

    useEffect(() => {
        if (invoiceData && contacts.length > 0) {
            const matchedContact = contacts.find(c => String(c.idContact) === String(invoiceData.id_customer));
            if (matchedContact) {
                setSelectedContact(matchedContact);
                setStatusGrup(matchedContact.contactType === 0 ? 'Grup' : 'Non Grup');
                filterHandlingCosts();
            }

            // Populate lineItems from saved finance_invoice_details
            if (invoiceData.items && invoiceData.items.length > 0 && lineItems.length === 0) {
                const savedItems = invoiceData.items.map((d, idx) => ({
                    id: d.id_detail || `saved-${idx}`,
                    name: d.item_name || '',
                    description: '',
                    qty: Number(d.qty || 0),
                    price: Number(d.unit_price || 0),
                    taxRate: Number(d.tax_rate || 0),
                    taxAmount: Number(d.tax_amount || 0),
                    amountBeforeTax: Number(d.sub_amount || 0),
                    total: Number(d.total_amount || 0),
                }));
                setLineItems(savedItems);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [invoiceData, contacts]);

    const filterHandlingCosts = () => {
        const filtered = costs.filter(c =>
            c.item_name?.toUpperCase().includes('HANDLING')
        );
        setFilteredCosts(filtered);
    };

    useEffect(() => {
        if (costs.length > 0) {
            filterHandlingCosts();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [costs]);

    // ─── Handling Services ───
    const handleAddService = () => {
        if (!selectedServiceId) return;
        const cost = costs.find(c => c.id_cost === selectedServiceId);
        if (!cost) return;

        const price = Number(cost.unit_price) || 0;
        const taxRate = parseFloat(cost.tax_rate) || 0;
        const amountBeforeTax = price * 1;
        const taxAmount = amountBeforeTax * (taxRate / 100);

        const newItem = {
            id: selectedServiceId,
            name: cost.cost_name,
            description: cost.item_name,
            qty: 1,
            price,
            taxRate,
            amountBeforeTax,
            taxAmount,
            total: amountBeforeTax + taxAmount
        };
        setLineItems([...lineItems, newItem]);
        setSelectedServiceId('');
    };

    const handleRemoveItem = (idx) => {
        const ni = [...lineItems];
        ni.splice(idx, 1);
        setLineItems(ni);
    };

    const handleQtyChange = (idx, val) => {
        const ni = [...lineItems];
        ni[idx].qty = val;
        ni[idx].amountBeforeTax = val * ni[idx].price;
        ni[idx].taxAmount = ni[idx].amountBeforeTax * (ni[idx].taxRate / 100);
        ni[idx].total = ni[idx].amountBeforeTax + ni[idx].taxAmount;
        setLineItems(ni);
    };

    const handlePriceChange = (idx, val) => {
        const ni = [...lineItems];
        ni[idx].price = val;
        ni[idx].amountBeforeTax = val * ni[idx].qty;
        ni[idx].taxAmount = ni[idx].amountBeforeTax * (ni[idx].taxRate / 100);
        ni[idx].total = ni[idx].amountBeforeTax + ni[idx].taxAmount;
        setLineItems(ni);
    };

    // ─── Terminal Fee ───
    const handleTerminalChange = (val) => {
        setFieldValue('terminal', val);
        if (!val || !terminalFields[val]) return;

        const chargeNames = terminalFields[val];
        const newItems = [];
        const ignoredLabels = [
            "TERMINAL CHARGE :", "Terminal Charge", "DETAIL OF CHARGE :",
            "KOJA", "NCT1", "TPK1", "TMAL", "TER3", "PESA", "CIMP", "RAYA", "PRJP", "BAND", "MTI1", "WIRA"
        ];

        chargeNames.forEach(name => {
            if (ignoredLabels.some(l => l.toUpperCase() === name.trim().toUpperCase())) return;
            const cost = costs.find(c => c.cost_name?.trim().toUpperCase() === name.trim().toUpperCase());
            if (cost) {
                const price = Number(cost.unit_price) || 0;
                const taxRate = parseFloat(cost.tax_rate) || 0;
                const amountBeforeTax = price;
                const taxAmount = amountBeforeTax * (taxRate / 100);
                newItems.push({
                    id: cost.id_cost, name: cost.cost_name, description: cost.item_name,
                    qty: 1, price, taxRate, amountBeforeTax, taxAmount,
                    total: amountBeforeTax + taxAmount, isEditable: price === 0
                });
            }
        });

        if (newItems.length > 0) {
            setLineItems(prev => [...prev, ...newItems]);
            message.success(`Added ${newItems.length} charges for ${val}`);
        }
    };

    // ─── DETIL ITEM (finance_invoice_item) ───
    const handleAddInvoiceItem = async () => {
        if (!selectedItemCode || !id) return;
        const item = nopenItems.find(i => `${i.item_code}|${i.lokasi_lot}` === selectedItemCode);
        if (!item) return;

        try {
            const res = await axiosInstance.post('/finance/invoice-items', {
                id_invoice: Number(id),
                item_code: item.item_code,
                item_name: item.item_name,
                jumlah_kemasan: item.jumlah_kemasan,
                lokasi_lot: item.lokasi_lot,
            });
            setInvoiceItems(prev => [...prev, {
                id_invoice_item: res.data.data.id_invoice_item,
                ...item,
            }]);
            setSelectedItemCode('');
            message.success('Item ditambahkan');
        } catch (err) {
            message.error('Gagal menambahkan item');
        }
    };

    const handleDeleteInvoiceItem = async (itemId) => {
        try {
            await axiosInstance.delete(`/finance/invoice-items/${itemId}`);
            setInvoiceItems(prev => prev.filter(i => i.id_invoice_item !== itemId));
            message.success('Item dihapus');
        } catch (err) {
            message.error('Gagal menghapus item');
        }
    };

    // ─── Totals ───
    const totalAll = lineItems.reduce((acc, curr) => acc + curr.total, 0);
    const totalPajak = lineItems.reduce((acc, curr) => acc + curr.taxAmount, 0);

    const handleSubmit = async () => {
        setLoading(true);
        try {
            const payload = {
                ...values,
                // Map lineItems to match exact backend DB keys
                items: lineItems.map(p => ({
                    item_name: p.description ? `${p.name} (${p.description})` : p.name,
                    qty: p.qty,
                    unit_price: p.price,
                    sub_amount: p.amountBeforeTax || 0,
                    tax_rate: p.taxRate || 0,
                    tax_amount: p.taxAmount || 0,
                    total_amount: p.total
                })),
                totalAmount: totalAll,
                invoiceDate: values.invoiceDate.format('YYYY-MM-DD'),
                dueDate: values.dueDate.format('YYYY-MM-DD'),
                periodStart: null,
                periodEnd: null,
            };

            if (id) {
                await axiosInstance.put(`/finance/sales-invoices/${id}`, payload);
                message.success('Invoice berhasil disimpan!');
                setTimeout(() => history.push('/finance'), 1500);
            }
        } catch (err) {
            console.error(err);
            message.error('Gagal menyimpan invoice');
        } finally {
            setLoading(false);
            setConfirmOpen(false);
        }
    };

    const getInvoiceTypeLabel = () => 'Handling';

    // Items available to add (exclude already-added ones)
    const availableItems = nopenItems.filter(ni =>
        !invoiceItems.some(ii => ii.item_code === ni.item_code && ii.lokasi_lot === ni.lokasi_lot)
    );

    return (
        <>
            <div style={{ marginBottom: 16 }}>
                <Title level={3}>Edit Invoice Handling</Title>
                <Text type="secondary">Finance / Edit / Handling</Text>
            </div>

            <Card>
                {initLoading ? <Skeleton active /> : (
                    <div>
                        <Row gutter={[16, 24]}>
                            {/* ══════ SECTION 1: DATA INVOICE ══════ */}
                            <Col span={24}>
                                <InvoiceHeader
                                    invoiceData={invoiceData}
                                    values={values}
                                    setFieldValue={setFieldValue}
                                    getInvoiceTypeLabel={getInvoiceTypeLabel}
                                />
                            </Col>

                            <Col span={24}>
                                <CustomerSection
                                    selectedContact={selectedContact}
                                    statusGrup={statusGrup}
                                />
                            </Col>

                            <Col span={24}>
                                <Divider />
                            </Col>

                            {/* ══════ SECTION 2: DETIL LAYANAN DAN BIAYA ══════ */}
                            <Col span={24}>
                                <Title level={5} style={{ marginBottom: 16 }}>DETIL LAYANAN DAN BIAYA</Title>
                            </Col>

                            {/* Terminal Fee */}
                            <Col span={24}>
                                <Text strong>Terminal Fee</Text>
                                <Select
                                    style={{ width: 400, marginTop: 4, display: 'block' }}
                                    value={values.terminal}
                                    onChange={handleTerminalChange}
                                    placeholder="Pilih Terminal"
                                    allowClear
                                >
                                    {Object.keys(terminalFields).map(key => (
                                        <Select.Option key={key} value={key}>{key}</Select.Option>
                                    ))}
                                </Select>
                            </Col>

                            <Col span={24} style={{ marginTop: 8 }} />

                            {/* Handling Services */}
                            <Col span={24}>
                                <Text strong>Handling Services</Text>
                                <Space style={{ width: '100%', marginTop: 8 }} size="middle">
                                    <Select
                                        style={{ width: 400 }}
                                        value={selectedServiceId}
                                        onChange={(value) => setSelectedServiceId(value)}
                                        placeholder="Pilih Layanan Handling"
                                        optionLabelProp="label"
                                    >
                                        {filteredCosts
                                            .filter(c => c.cost_name !== 'Botol')
                                            .map(c => (
                                                <Select.Option key={c.id_cost} value={c.id_cost} label={c.cost_name}>
                                                    <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                        <Text strong>{c.cost_name}</Text><br />
                                                        <Text type="secondary" style={{ fontSize: '12px' }}>{c.item_name}</Text>
                                                    </div>
                                                </Select.Option>
                                            ))}
                                    </Select>
                                    <Button
                                        icon={<PlusOutlined />}
                                        onClick={handleAddService}
                                        disabled={!selectedServiceId}
                                    >
                                        Tambah
                                    </Button>
                                </Space>
                            </Col>

                            {/* Service Table (lineItems from finance_invoice_details) */}
                            <Col span={24}>
                                <ServiceTable
                                    lineItems={lineItems}
                                    handleQtyChange={handleQtyChange}
                                    handlePriceChange={handlePriceChange}
                                    handleRemoveItem={handleRemoveItem}
                                />
                            </Col>

                            <Col span={24}>
                                <Divider />
                            </Col>

                            {/* ══════ SECTION 3: DETIL ITEM ══════ */}
                            <Col span={24}>
                                <Title level={5} style={{ marginBottom: 16 }}>DETIL ITEM</Title>
                                <Space style={{ width: '100%', marginBottom: 12 }} size="middle">
                                    <Select
                                        style={{ width: 500 }}
                                        value={selectedItemCode}
                                        onChange={(val) => setSelectedItemCode(val)}
                                        placeholder="Pilih item dari nopen..."
                                        showSearch
                                        optionFilterProp="children"
                                    >
                                        {availableItems.map((item, idx) => (
                                            <Select.Option
                                                key={`${item.item_code}|${item.lokasi_lot}|${idx}`}
                                                value={`${item.item_code}|${item.lokasi_lot}`}
                                            >
                                                {item.item_code} — {item.item_name} (Lot: {item.lokasi_lot || '-'}, Qty: {item.jumlah_kemasan})
                                            </Select.Option>
                                        ))}
                                    </Select>
                                    <Button
                                        icon={<PlusOutlined />}
                                        onClick={handleAddInvoiceItem}
                                        disabled={!selectedItemCode}
                                    >
                                        Tambah Item
                                    </Button>
                                </Space>

                                <Table
                                    dataSource={invoiceItems}
                                    rowKey={(r) => r.id_invoice_item || `${r.item_code}-${r.lokasi_lot}`}
                                    pagination={false}
                                    size="small"
                                    columns={[
                                        { title: 'Kode Barang', dataIndex: 'item_code', key: 'item_code', width: 150 },
                                        { title: 'Nama Barang', dataIndex: 'item_name', key: 'item_name' },
                                        { title: 'Jumlah Kemasan', dataIndex: 'jumlah_kemasan', key: 'jumlah_kemasan', width: 140, align: 'center' },
                                        { title: 'Lokasi Lot', dataIndex: 'lokasi_lot', key: 'lokasi_lot', width: 120 },
                                        {
                                            title: '#', key: 'action', width: 60, align: 'center',
                                            render: (_, record) => (
                                                <Popconfirm title="Hapus item ini?" onConfirm={() => handleDeleteInvoiceItem(record.id_invoice_item)}>
                                                    <Button size="small" danger type="text" icon={<DeleteOutlined />} />
                                                </Popconfirm>
                                            )
                                        },
                                    ]}
                                    locale={{ emptyText: 'Belum ada item. Pilih dari dropdown di atas.' }}
                                />
                            </Col>

                            <Col span={24}>
                                <Divider />
                            </Col>

                            {/* ══════ SECTION 4: DPP / PPN / PPh / TOTAL ══════ */}
                            <Col span={24}>
                                <TotalSummary 
                                    totalAll={totalAll} 
                                    totalPajak={totalPajak} 
                                    otherAmount={invoiceData?.other_amount} 
                                />
                            </Col>

                            <Col span={24} style={{ textAlign: 'right' }}>
                                <Space>
                                    <Button size="large" onClick={() => history.push('/finance')}>
                                        Batal
                                    </Button>
                                    <Button
                                        type="primary"
                                        size="large"
                                        onClick={() => setConfirmOpen(true)}
                                    >
                                        Simpan
                                    </Button>
                                </Space>
                            </Col>
                        </Row>

                        <Modal
                            title="Konfirmasi Simpan"
                            open={confirmOpen}
                            onOk={handleSubmit}
                            onCancel={() => setConfirmOpen(false)}
                            okText="Ya, Simpan"
                            cancelText="Batal"
                        >
                            <p>Apakah anda yakin ingin menyimpan perubahan invoice ini?</p>
                        </Modal>
                    </div>
                )}
            </Card>
        </>
    );
}
